import { useEffect } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import Row from '@sdk/components/ui/spacing/Row';
import useAuth from '@sdk/authentication/useAuth';
import Picker from '@sdk/components/forms/Picker';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import dateToDayMonthYear from '@sdk/utils/dateFormat/dateToDayMonthYear';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { PayrollPeriodType } from '@sdk/labor/types';
import { LABOR_COST_CALCULATOR_OPTIONS } from '@sdk/common/constants';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { LaborCostSettings, UpdateLaborCostSettings } from '@sdk/labor/types';

import { OutputDateFormatType } from '@web/forms/components/datePicker/datePicker.types';
import DatePickerField from '@web/forms/components/datePicker/DatePickerField/DatePickerField';

import RowSpaceBetween from '../RowSpaceBetween';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import {
  FIELD_WIDTH_205,
  PAYROLL_PERIOD_OPTIONS,
  SETTINGS_STAGING_BG_COLOR,
} from '../../constants';
import { useSettingsErrorsContext } from '../../SettingsErrorContext';
import { TabType } from '../../types';

import CurrencyPicker from './CurrencyPicker';

const StyledRow = styled(Row)`
  align-items: center;
`;

const StagingSettingsSection = styled(SettingsSection)`
  background: ${SETTINGS_STAGING_BG_COLOR};
  margin-top: -20px;
  margin-bottom: 20px;
`;

type Props = {
  laborSettings: LaborCostSettings;
  currencyCode: string;
  payrollPeriodType: PayrollPeriodType;
  payrollPeriodStartDate: Date;
  payrollPeriodStartDateTwo?: Date;
  onLaborCostSettingsChange: UpdateLaborCostSettings;
};

const GeneralSettings: React.FC<React.PropsWithChildren<Props>> = ({
  laborSettings,
  currencyCode,
  payrollPeriodType,
  payrollPeriodStartDate,
  payrollPeriodStartDateTwo,
  onLaborCostSettingsChange,
}) => {
  const { currentUser } = useAuth();
  const { toggleError, isFieldValid } = useSettingsErrorsContext();

  useEffect(() => {
    if (payrollPeriodType === PayrollPeriodType.semiMonthly) {
      toggleError(
        TabType.laborCost,
        'payrollPeriod',
        payrollPeriodStartDate.getTime() === payrollPeriodStartDateTwo?.getTime()
      );
      toggleError(TabType.laborCost, 'payrollPeriodType', !payrollPeriodStartDateTwo);
    }
  }, [payrollPeriodStartDate, payrollPeriodStartDateTwo, payrollPeriodType, toggleError]);

  return (
    <>
      <SettingsSection withSeparator>
        <RowSpaceBetween>
          <Text14 semiBold themeColor={ThemeColorType.gray}>
            {gettext('Currency')}
          </Text14>
          <CurrencyPicker
            currency={currencyCode}
            disabled={!currentUser.isAdmin}
            onChange={(value: CurrencyType): void =>
              onLaborCostSettingsChange({ currency: value.code })
            }
            customWidth={FIELD_WIDTH_205}
          />
        </RowSpaceBetween>
      </SettingsSection>
      <SettingsSection withSeparator>
        <Column pBottom={20}>
          <RowSpaceBetween>
            <Column>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {gettext('Payroll period type')}
              </Text14>
              <Text14 themeColor={ThemeColorType.gray}>
                {gettext('Choose the period you would like to use for your company’s labor cost:')}
              </Text14>
            </Column>
            <Picker
              disabled={!currentUser.isAdmin}
              options={PAYROLL_PERIOD_OPTIONS}
              customWidth={FIELD_WIDTH_205}
              value={payrollPeriodType}
              onChange={(value: PayrollPeriodType): void => {
                onLaborCostSettingsChange({ payrollPeriodType: value });
              }}
            />
          </RowSpaceBetween>
        </Column>

        {payrollPeriodType === PayrollPeriodType.semiMonthly ? (
          <Column>
            <RowSpaceBetween>
              <Column>
                <Text14 semiBold themeColor={ThemeColorType.gray}>
                  {gettext('Payroll period')}
                </Text14>
                <Text14 themeColor={ThemeColorType.gray}>
                  <TranslateWithComponents
                    replaceComponents={{
                      startDate: (
                        <Text14 themeColor={ThemeColorType.primary}>
                          {dateToDayMonthYear(payrollPeriodStartDate, true)}
                        </Text14>
                      ),
                      endDate: (
                        <Text14 themeColor={ThemeColorType.primary}>
                          {payrollPeriodStartDateTwo
                            ? dateToDayMonthYear(payrollPeriodStartDateTwo, true)
                            : '-'}
                        </Text14>
                      ),
                    }}>
                    {gettext('Payroll periods begin on the [[startDate]] and [[endDate]]')}
                  </TranslateWithComponents>
                </Text14>
              </Column>
              <StyledRow>
                <DatePickerField
                  customWidth={130}
                  outputFormat={OutputDateFormatType.ddMMMyyyy}
                  date={payrollPeriodStartDate}
                  alwaysOpenOnToday
                  onDateChange={(date: Date): void =>
                    onLaborCostSettingsChange({ payrollPeriodStartDate: date })
                  }
                  disabled={!currentUser.isAdmin}
                  invalid={!isFieldValid('payrollPeriod')}
                />

                <Text14 themeColor={ThemeColorType.gray} pRight={10} pLeft={10}>
                  {gettext('and')}
                </Text14>

                <DatePickerField
                  customWidth={130}
                  outputFormat={OutputDateFormatType.ddMMMyyyy}
                  date={payrollPeriodStartDateTwo}
                  alwaysOpenOnToday
                  onDateChange={(date: Date): void =>
                    onLaborCostSettingsChange({ payrollPeriodStartDateTwo: date })
                  }
                  invalid={!isFieldValid('payrollPeriod') || !isFieldValid('payrollPeriodType')}
                  placeholder={gettext('Specify date')}
                  disabled={!currentUser.isAdmin}
                />
              </StyledRow>
            </RowSpaceBetween>
          </Column>
        ) : (
          <Column>
            <RowSpaceBetween>
              <Column>
                <Text14 semiBold themeColor={ThemeColorType.gray}>
                  {gettext('Start date')}
                </Text14>
                <Text14 themeColor={ThemeColorType.gray}>
                  {gettext('Select your next payroll date.')}
                </Text14>
              </Column>
              <Column>
                <DatePickerField
                  customWidth={FIELD_WIDTH_205}
                  outputFormat={OutputDateFormatType.ddMMMMyyyy}
                  date={payrollPeriodStartDate}
                  alwaysOpenOnToday
                  onDateChange={(date: Date): void =>
                    onLaborCostSettingsChange({ payrollPeriodStartDate: date })
                  }
                  disabled={!currentUser.isAdmin}
                />
              </Column>
            </RowSpaceBetween>
          </Column>
        )}
      </SettingsSection>

      {__STAGING__ ? (
        <>
          <StagingSettingsSection>
            <RowSpaceBetween pTop={20} pBottom={20}>
              <Text14 semiBold themeColor={ThemeColorType.gray}>
                {gettext('Calculator type')}
              </Text14>
              <Picker
                options={LABOR_COST_CALCULATOR_OPTIONS}
                value={laborSettings.laborCostCalculatorType}
                onChange={(value: string): void =>
                  onLaborCostSettingsChange({ laborCostCalculatorType: value })
                }
                customWidth={FIELD_WIDTH_205}
              />
            </RowSpaceBetween>
            <HorizontalSeparator />
          </StagingSettingsSection>
        </>
      ) : null}

      <SettingsSection pBottom={20}>
        <SettingsSwitcherRow
          title={gettext('Employee wages')}
          subtitle={gettext('Allow employees to see their own wages.')}
          value={laborSettings.usersCanSeeWages}
          onChange={(value: boolean): void =>
            onLaborCostSettingsChange({ usersCanSeeWages: value })
          }
          disabled={!currentUser.isAdmin}
        />
      </SettingsSection>
    </>
  );
};

export default GeneralSettings;
